import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { useConfigStore } from '../../../../stores';
import FormFieldError from '../FormComponents/FormFieldError';
import DetailTable from '../../../Detail/DetailTable';

// Prospect column cell.
//
// A search on a common surname comes back with family members, staff and
// vendor contacts sitting alongside the actual prospects. These two fields are
// what tells them apart, so the flag reads as a badge and the relationship
// labels everyone else. A row can be both, so neither excludes the other.
//
// Defined out here rather than inside the step: it renders once per result row,
// and a component redefined during the parent's render is a new type on every
// pass, which throws away each row's DOM.
const ProspectColumnCell = ({
  isProspect,
  relationshipToProspect,
  rowKey,
}) => {
  // Salesforce sends a real boolean here, but the field is read-only to the
  // YourTour integration user, so a record YourTour created reads false rather
  // than absent until Lansdale ticks it — an unbadged row is not proof the
  // person isn't a prospect.
  const isFlaggedProspect = isProspect === true || isProspect === 'true';
  const relationship = relationshipToProspect || '';

  return (
    <div
      className="personalization__crm-option personalization__crm-option--prospect"
      data-testid={`crm-result-prospect:${rowKey}`}
    >
      { isFlaggedProspect && (
        <span className="personalization__crm-prospect-badge">Prospect</span>
      )}

      { relationship !== '' && (
        <span className="personalization__crm-prospect-relationship">{relationship}</span>
      )}

      { !isFlaggedProspect && relationship === '' && (
        <span className="personalization__crm-prospect-empty">&mdash;</span>
      )}
    </div>
  );
};

ProspectColumnCell.propTypes = {
  isProspect: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  relationshipToProspect: PropTypes.string,
  rowKey: PropTypes.number.isRequired,
};

ProspectColumnCell.defaultProps = {
  isProspect: false,
  relationshipToProspect: '',
};

const AccountResidentSelectionStep = ({
  handleBlur,
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  crmData,
  residentSelected,
  setResidentSelected,
  footerNav,
}) => {
  const crmConfig = useConfigStore((state) => state.crmConfig);

  const { machineName } = crmConfig || {};

  useEffect(() => {
    const { residentSelection } = values;
    if (Number.isInteger(residentSelection)) {
      setResidentSelected(residentSelection);
    }
  }, [values, setResidentSelected]);

  // Select a prospect by row index, keeping Formik and the local highlight state
  // in sync. Shared by the row click (mouse) and the radio input (keyboard).
  const handleSelect = (key) => {
    setFieldValue('residentSelection', key);
    setResidentSelected(key);
  };

  // Drupal and Enquire matches continue to an edit step; every other CRM lands
  // in the session straight from here, so the copy and the button differ.
  const isEditableCrm = machineName === 'drupal' || machineName === 'enquire';

  // Form instructions, named for the button they tell the user to click.
  const Instructions = () => (isEditableCrm
    ? 'Select a prospect below and click CONTINUE to proceed. If your prospect isn’t listed, click "Or Create New Prospect". '
    : 'Select a prospect below and click START PERSONALIZATION to proceed. If your prospect isn’t listed, click "Or Create New Prospect". ');

  // Date column header label, varies by CRM.
  const dateHeaderLabel = machineName === 'sherpa' || machineName === 'enquire'
    ? 'Created'
    : 'Changed';

  // Lansdale is the only CRM whose search results carry the prospect flag and
  // the relationship — every other CRM's records omit both keys — so the column
  // only exists there.
  const showsProspectColumn = machineName === 'lansdale';

  // Format a row's changed date, or null if there's nothing to show.
  const formatChangedDate = (date) => {
    if (!date || date === '') {
      return null;
    }
    const dateObject = new Date(date);
    return new Intl.DateTimeFormat('en-US').format(dateObject) || null;
  };

  // Header row for the resident table. First column is the radio (no label).
  const tableHeadData = [
    '',
    'Person 1',
    'Email 1',
    'Person 2',
    'Email 2',
    ...(showsProspectColumn ? ['Prospect'] : []),
    dateHeaderLabel,
  ];

  // Show the star legend only when at least one entry has a prior YourTour session.
  const hasStarredEntries = crmData.some(
    ({ ytSessions }) => ytSessions && ytSessions.length > 0,
  );

  // One selectable row per CRM match.
  const tableRowData = crmData.map(({
    firstName,
    lastName,
    email,
    person2FirstName,
    person2LastName,
    person2Email,
    changed,
    ytSessions,
    isProspect,
    relationshipToProspect,
  }, key) => {
    const hasSession = ytSessions && ytSessions.length > 0;
    return {
      className: classNames({ 'detailTable__row--selected': residentSelected === key }),
      onClick: () => handleSelect(key),
      testId: `crm-result-row:${key}`,
      cells: [{
        data: (
          <input
            type="radio"
            // Unique name per row so each radio is its own group and stays an
            // independent tab stop. A shared name makes the browser treat the
            // group as a single roving tab stop (Tab hits only one, the rest
            // need arrow keys). Single-selection is enforced by `checked` state,
            // not the native group, so decoupling is safe.
            name={`residentSelection-${key}`}
            className="detailTable__radio"
            checked={residentSelected === key}
            onChange={() => handleSelect(key)}
            onBlur={handleBlur('residentSelection')}
            aria-label={`Select ${firstName} ${lastName}`}
            data-testid={`resident-radio:${key}`}
          />
        ),
      },
      {
        mobileLabel: 'Person 1',
        data: <>
          {`${firstName} ${lastName}`}
          {hasSession && '*'}
              </>,
      },
      { mobileLabel: 'Email 1', data: email, className: 'personalization__emailCell' },
      { mobileLabel: 'Person 2', data: `${person2FirstName ?? ''} ${person2LastName ?? ''}` },
      { mobileLabel: 'Email 2', data: person2Email, className: 'personalization__emailCell' },
      ...(showsProspectColumn ? [{
        mobileLabel: 'Prospect',
        data: (
          <ProspectColumnCell
            isProspect={isProspect}
            relationshipToProspect={relationshipToProspect}
            rowKey={key}
          />
        ),
      }] : []),
      { mobileLabel: dateHeaderLabel, data: formatChangedDate(changed) }],
    };
  });

  // Clear everything the previous selection filled in, so "Create New" starts
  // from an empty form rather than the matched prospect's details.
  const clearSelection = () => {
    setFieldValue('action', 'create new');
    setFieldValue('drupal_uuid', '');
    setFieldValue('first_name', '');
    setFieldValue('last_name', '');
    setFieldValue('email', '');
    setFieldValue('my_favorites_page', '');
    setFieldValue('person2_drupal_uuid', '');
    setFieldValue('person2_first_name', '');
    setFieldValue('person2_last_name', '');
    setFieldValue('person2_email', '');
    setFieldValue('resident', '');
    setResidentSelected('');
  };

  return (
    <div data-testid="personalization-step:selection">
      <div className="modal__section">
        <h1 className="modal__subsection modal__title modal__title--short">Personalize YourTour</h1>
        <div className="modal__subsection modal__description modal__description--short">
          <Instructions />
        </div>
      </div>

      <div className="modal__section">

        <DetailTable
          className="personalization__crmTable modal__subsection"
          tableHeadData={tableHeadData}
          tableRowData={tableRowData}
          tableNote={hasStarredEntries ? '*Has previously taken a YourTour' : null}
        />

        <div
          className="modal__subsection modal__description modal__description--short"
          data-testid="crm-result-summary"
        >
          {crmData.length === 1 ? 'There is ' : 'There are '}
          <b>
            {crmData.length}
            {' '}
            {crmData.length === 1 ? 'prospect' : 'prospects'}
          </b>
          {' '}
          matching your search.
        </div>

        {errors.residentSelection && touched.residentSelection && (
          <div className="modal__subsection">
            <FormFieldError errorMsg={errors.residentSelection} />
          </div>
        )}

      </div>

      <div className="modal__section modal__btns modal__btns--inline">

        { isEditableCrm ? (
          <button
            disabled={isSubmitting}
            type="submit"
            className="modal__btn modal__btn--secondary"
            onClick={() => {
              setFieldValue('action', 'continue');
            }}
            data-testid="personalization-select-continue"
          >
            Continue
          </button>
        ) : (
          <button
            disabled={isSubmitting}
            type="submit"
            className="modal__btn modal__btn--secondary"
            onClick={() => {
              // Clear a 'create new' left over from an earlier pass through
              // this step, or the submit handler routes to the create form
              // instead of the selection the user just made.
              setFieldValue('action', 'continue');
            }}
            data-testid="personalization-start"
          >
            Start Personalization
          </button>
        )}

        <button
          disabled={isSubmitting}
          type="submit"
          className="modal__textBtn modal__textBtn--secondary"
          onClick={clearSelection}
          data-testid="personalization-create-new"
        >
          Or Create New Prospect
        </button>
      </div>

      {footerNav}
    </div>
  );
};

export default AccountResidentSelectionStep;

AccountResidentSelectionStep.propTypes = {
  handleBlur: PropTypes.func.isRequired,
  values: PropTypes.shape({
    residentSelection: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  errors: PropTypes.shape({
    residentSelection: PropTypes.string,
  }).isRequired,
  touched: PropTypes.shape({}).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  crmData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  residentSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setResidentSelected: PropTypes.func.isRequired,
  footerNav: PropTypes.node,
};

AccountResidentSelectionStep.defaultProps = {
  residentSelected: '',
  footerNav: null,
};
